import React, { useState } from 'react';
import './App.css';
import SearchComponent from './components/SearchComponent';
import ManufacturerSearchComponent from './components/ManufacturerSearchComponent';
import ComparisonComponent from './components/ComparisonComponent';
import DashboardComponent from './components/DashboardComponent';

function OverviewSection() {
  const sectionCard = {
    background: 'rgba(255,255,255,.03)',
    border: '1px solid rgba(255,255,255,.06)',
    borderRadius: '16px',
    padding: '28px',
    marginBottom: '24px',
    backdropFilter: 'blur(10px)'
  };
  const heading = (color) => ({
    fontSize: '13px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    color: color,
    marginBottom: '12px'
  });
  const title = {
    fontSize: '22px',
    fontWeight: '700',
    color: '#f1f5f9',
    marginBottom: '12px',
    lineHeight: '1.3'
  };
  const body = {
    fontSize: '14px',
    color: '#94a3b8',
    lineHeight: '1.8'
  };
  const techBadge = {
    display: 'inline-block',
    padding: '4px 12px',
    background: 'rgba(99,102,241,.12)',
    border: '1px solid rgba(99,102,241,.2)',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#a5b4fc',
    margin: '3px'
  };

  return (
    <div>
      <div style={{
        textAlign: 'center',
        padding: '50px 20px 40px',
        background: 'radial-gradient(ellipse at top, rgba(99,102,241,.08) 0%, transparent 60%)'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚗</div>
        <h1 style={{ fontSize: '36px', fontWeight: '800', color: '#6366f1', marginBottom: '8px' }}>Vehicle Reliability Analytics</h1>
        <p style={{ color: '#94a3b8', fontSize: '15px', marginBottom: '32px' }}>
          NHTSA complaint data transformed into actionable reliability intelligence using<br/>
          Weibull survival analysis and predictive modeling
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '40px' }}>
          <div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#f1f5f9' }}>10,507</div>
            <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px' }}>Complaints Analyzed</div>
          </div>
          <div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#f1f5f9' }}>971</div>
            <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px' }}>Unique Vehicles</div>
          </div>
          <div>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#f1f5f9' }}>8</div>
            <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px' }}>Manufacturers</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 20px 40px' }}>

        <div style={sectionCard}>
          <div style={heading('#ef4444')}>🔴 The Problem</div>
          <div style={title}>Fleet managers lack data-driven reliability intelligence</div>
          <p style={body}>
            NHTSA collects thousands of vehicle safety complaints annually, but this data sits in flat CSV files with no analytics layer. Fleet operators, insurance companies, and consumers have no way to:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginTop: '20px' }}>
            <div style={{ ...sectionCard, borderTop: '3px solid #6366f1', marginBottom: 0 }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>🏭</div>
              <div style={{ fontWeight: '700', color: '#f1f5f9', marginBottom: '6px' }}>Compare Manufacturers</div>
              <div style={{ fontSize: '13px', color: '#94a3b8' }}>No normalized reliability scores across makes — raw complaint counts mislead because they ignore fleet size and vehicle age</div>
            </div>
            <div style={{ ...sectionCard, borderTop: '3px solid #f59e0b', marginBottom: 0 }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚠️</div>
              <div style={{ fontWeight: '700', color: '#f1f5f9', marginBottom: '6px' }}>Predict Failures</div>
              <div style={{ fontSize: '13px', color: '#94a3b8' }}>No forward-looking risk estimates — stakeholders react to complaints after they happen instead of anticipating component failures</div>
            </div>
            <div style={{ ...sectionCard, borderTop: '3px solid #3b82f6', marginBottom: 0 }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔍</div>
              <div style={{ fontWeight: '700', color: '#f1f5f9', marginBottom: '6px' }}>Analyze Individual VINs</div>
              <div style={{ fontSize: '13px', color: '#94a3b8' }}>No per-vehicle risk profiles — each VIN's complaint history, component patterns, and comparative position are invisible</div>
            </div>
          </div>
        </div>

        <div style={sectionCard}>
          <div style={heading('#22c55e')}>🟢 Architecture</div>
          <div style={title}>Full-stack analytics pipeline: ingestion → modeling → visualization</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginTop: '20px', marginBottom: '20px' }}>
            {[
              { num: '01', color: '#6366f1', name: 'Data Ingestion', desc: 'NHTSA CSV → Pandas DataFrame' },
              { num: '02', color: '#22c55e', name: 'Reliability Engine', desc: 'Exponential decay model normalized by vehicle age' },
              { num: '03', color: '#ef4444', name: 'Risk Prediction', desc: 'Poisson-based 1yr/3yr/5yr failure probability' },
              { num: '04', color: '#f59e0b', name: 'Interactive Dashboard', desc: 'React + Flask REST API with live queries' }
            ].map((step) => (
              <div key={step.num} style={{ ...sectionCard, borderTop: '3px solid ' + step.color, marginBottom: 0, textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: '800', color: step.color, marginBottom: '8px' }}>{step.num}</div>
                <div style={{ fontWeight: '700', color: '#f1f5f9', marginBottom: '4px' }}>{step.name}</div>
                <div style={{ fontSize: '12px', color: '#94a3b8' }}>{step.desc}</div>
              </div>
            ))}
          </div>
          <div>
            {['Python','Flask','Pandas','React','Axios','SVG Charts','REST API','NHTSA Open Data','Exponential Decay','Poisson Model','CORS','Responsive CSS'].map((t) => (
              <span key={t} style={techBadge}>{t}</span>
            ))}
          </div>
        </div>

        <div style={sectionCard}>
          <div style={heading('#3b82f6')}>🔵 The Solution</div>
          <div style={title}>Three analytical tools that turn raw complaints into decisions</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '20px' }}>
            <div style={{ ...sectionCard, borderTop: '3px solid #6366f1', marginBottom: 0 }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>🏭</div>
              <div style={{ fontWeight: '700', color: '#f1f5f9', marginBottom: '6px' }}>Manufacturer Browser</div>
              <div style={{ fontSize: '13px', color: '#94a3b8' }}>Browse all vehicles by make. Fleet overview with computed reliability scores, MTTF from actual complaint intervals, and per-vehicle drill-down.</div>
            </div>
            <div style={{ ...sectionCard, borderTop: '3px solid #22c55e', marginBottom: 0 }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚖️</div>
              <div style={{ fontWeight: '700', color: '#f1f5f9', marginBottom: '6px' }}>Manufacturer Comparison</div>
              <div style={{ fontSize: '13px', color: '#94a3b8' }}>Side-by-side comparison with normalized metrics: reliability %, complaints/vehicle, MTTF. Checkmark indicators show which manufacturer wins each metric.</div>
            </div>
            <div style={{ ...sectionCard, borderTop: '3px solid #f59e0b', marginBottom: 0 }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔍</div>
              <div style={{ fontWeight: '700', color: '#f1f5f9', marginBottom: '6px' }}>Vehicle Deep Dive</div>
              <div style={{ fontSize: '13px', color: '#94a3b8' }}>Per-VIN analytics: reliability score, predictive risk (1/3/5yr), component breakdown donut chart, complaint timeline, AI-generated insights, and comparative analysis.</div>
            </div>
          </div>
        </div>

        <div style={sectionCard}>
          <div style={heading('#f59e0b')}>🟠 Key Formulas</div>
          <div style={title}>Defensible, transparent reliability metrics</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '20px' }}>
            <div style={{ ...sectionCard, borderTop: '3px solid #6366f1', marginBottom: 0 }}>
              <div style={{ fontWeight: '700', color: '#6366f1', marginBottom: '8px' }}>Vehicle Reliability</div>
              <div style={{ fontFamily: 'monospace', fontSize: '15px', color: '#f1f5f9', marginBottom: '8px' }}>R = 100 x e^(-0.3 x complaints/year)</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>1 complaint/yr = 74% | 3/yr = 41% | 10/yr = 5%</div>
            </div>
            <div style={{ ...sectionCard, borderTop: '3px solid #22c55e', marginBottom: 0 }}>
              <div style={{ fontWeight: '700', color: '#22c55e', marginBottom: '8px' }}>Manufacturer Reliability</div>
              <div style={{ fontFamily: 'monospace', fontSize: '15px', color: '#f1f5f9', marginBottom: '8px' }}>R = 100 x e^(-0.05 x avg_complaints/vehicle)</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Honda (9.0 cpv) = 64% | Ford (18.2 cpv) = 40%</div>
            </div>
            <div style={{ ...sectionCard, borderTop: '3px solid #ef4444', marginBottom: 0 }}>
              <div style={{ fontWeight: '700', color: '#ef4444', marginBottom: '8px' }}>Predictive Risk</div>
              <div style={{ fontFamily: 'monospace', fontSize: '15px', color: '#f1f5f9', marginBottom: '8px' }}>P(N yr) = 1 - e^(-rate x N)</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Rate 0.5/yr = 1yr: 39% | 3yr: 78% | 5yr: 92%</div>
            </div>
            <div style={{ ...sectionCard, borderTop: '3px solid #f59e0b', marginBottom: 0 }}>
              <div style={{ fontWeight: '700', color: '#f59e0b', marginBottom: '8px' }}>MTTF</div>
              <div style={{ fontFamily: 'monospace', fontSize: '15px', color: '#f1f5f9', fontStyle: 'italic', marginBottom: '8px' }}>Mean interval between first and last complaint</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Computed from actual dateOfIncident, not hardcoded</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

function App() {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const tabs = [
    { id: 'overview', label: '🏠 Overview' },
    { id: 'vin', label: '🔍 Search by VIN' },
    { id: 'manufacturer', label: '🏭 Browse Manufacturers' },
    { id: 'compare', label: '⚖️ Compare' }
  ];

  return (
    <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1a1b2e 100%)', minHeight: '100vh', color: '#e2e8f0', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <nav style={{
        background: 'rgba(255,255,255,.02)',
        borderBottom: '1px solid rgba(255,255,255,.06)',
        padding: '14px 20px',
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setSelectedVehicle(null); }}
            style={{
              background: activeTab === tab.id ? '#6366f1' : 'rgba(255,255,255,.05)',
              color: activeTab === tab.id ? '#fff' : '#94a3b8',
              border: activeTab === tab.id ? '1px solid #6366f1' : '1px solid rgba(255,255,255,.06)',
              padding: '8px 14px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              fontFamily: 'inherit',
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="tab-content">
        {activeTab === 'overview' && <OverviewSection />}
        {activeTab === 'vin' && <SearchComponent onSelectVehicle={setSelectedVehicle} />}
        {activeTab === 'manufacturer' && <ManufacturerSearchComponent onSelectVehicle={setSelectedVehicle} />}
        {activeTab === 'compare' && <ComparisonComponent />}
      </div>

      {activeTab !== 'compare' && activeTab !== 'overview' && selectedVehicle && (
        <DashboardComponent vehicle={selectedVehicle} />
      )}

      <div style={{textAlign:'center',color:'#64748b',fontSize:'11px',padding:'24px 20px 12px',borderTop:'1px solid rgba(255,255,255,0.06)',marginTop:'40px'}}>
        Disclaimer: This application uses publicly available data from the NHTSA complaint database for experimental and educational purposes only. Not intended for commercial use. Reliability scores and predictions do not represent official NHTSA findings or manufacturer quality assessments.
      </div>
      <div style={{textAlign:'center',color:'#94a3b8',fontSize:'12px',padding:'0 20px 32px'}}>
        Built by <strong style={{color:'#f1f5f9'}}>Md Sohel Ahmed, Ph.D.</strong>
        <br/>
        <span style={{fontSize:'11px',color:'#64748b'}}>
          <a href="https://dl.acm.org/doi/10.1145/3368089.3417043" target="_blank" rel="noopener noreferrer" style={{color:'#6366f1',textDecoration:'none'}}>Testing Machine Learning Code Using Polyhedral Region</a>{' — Ahmed, M.S., Ishikawa, F., Sugiyama, M. · ESEC/FSE 2020 · ACM'}
          <br/>
        </span>
      </div>
    </div>
  );
}

export default App;
